import React from 'react';
import _ from 'lodash';
import moment from 'moment';

import Field from '../../Fields/Field';
import InputTextField from '../../Fields/InputTextField';
import InputCurrencyField from '../../Fields/InputCurrencyField';
import DataTable from '../../Fields/DataTable';

import ActionButton from '../../Common/ActionButton';
import ProcessingIndicator from '../../Common/ProcessingIndicator';

import { PaymentHistory } from '../../../Utils/PaymentHistory';

import '../Page.scss';
import './SubscriptionDetailHistoryPanel.scss';
import {KeyboardDatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';

const historyDataTableColumns = [
  { id: 'date', heading: 'Trans. Date' },
  { id: 'applicationDate', heading: 'App. Date' },
  { id: 'type', heading: 'Type' },
  { id: 'invoiceLink', heading: 'Invoice Number' },
  { id: 'creditLink', heading: 'Credit ID' },
  { id: 'paymentId', heading: 'Payment ID' },
  { id: 'amount', heading: 'Amount' },
  { id: 'balance', heading: 'Current Balance' },
  { id: 'creditAmount', heading: 'Credit Amount' },
  { id: 'notes', heading: 'Transaction Notes' },
  { id: 'invoiceId', heading: null },
  { id: 'invoiceNumber', heading: null },
  { id: 'creditId', heading: null },
  { id: 'creditNumber', heading: null },
];

class SubscriptionDetailHistoryPanel extends React.Component {
  state = {
    panelInitializationComplete: false,
    panelInitializationErrors: [],
    subscriptionId: null,
    historyDataTableRows: [],
    totalBalance: 0,
    totalCredits: 0,
    queryInProgress: false,
    exportQueryInProgress: false,
    queryErrors: [],
    filters: {
      invoice_number: null,
      start_date: null,
      end_date: null,
      show_unpaid_invoices: null
    }
  };

  queryMetadata = {
    sortedBy: null,
    limit: null,
    total: null,
    offset: 0,
  };

  paymentHistory = null;
  facilityId;

  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);
    this.facilityId = urlSearchParams.get('facility');
  }

  async componentDidMount() {
    const {
      subscriptionId = null
    } = this.props;

    this.paymentHistory = new PaymentHistory({
      subscriptionId,
      filters: { ...this.state.filters },
    });
    await this.paymentHistory.initialize();

    try {
      this.setState({
        subscriptionId,
        panelInitializationComplete: true
      }, () => {
        this.refreshHistoryDataTable(true);
      });
    } catch (error) {
      this.setState({
        panelInitializationComplete: true,
        panelInitializationErrors: [error]
      });
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!_.isEqual(prevState.filters, this.state.filters)) {
      this.refreshHistoryDataTable(true);
    }
  }

  refreshHistoryDataTable(setBalance = false) {
    this.setState({
      queryInProgress: true
    }, async () => {
      try {
        let { totalBalance } = this.state;

        this.paymentHistory.filters = { ...this.state.filters };
        const results = await this.paymentHistory.fetchAll();
        const historyDataTableRows = this.prepareResultRows(results);
        if (setBalance) {
          totalBalance = this.getTotalBalance(results.invoices);
        }

        const totalCredits = this.getTotalCreditEntries(results.creditEntries);

        this.setState({
          queryInProgress: false,
          historyDataTableRows,
          totalBalance,
          totalCredits
        });
      } catch (errors) {
        this.setState({
          queryInProgress: false,
          queryErrors: errors
        });
      }
    });
  }

  getTotalBalance(invoices) {
    const { fieldConfiguration } = this.paymentHistory;
    const invoiceTypes = fieldConfiguration.invoice.type.options.map(option => option.value);
    return invoices.reduce((total, invoice) => {
      return invoiceTypes.includes(invoice.type) ? (total + invoice.balance) : total;
    }, 0);
  }

  getTotalCreditEntries(creditEntries) {
    const uniqueCredits = _.uniqBy(creditEntries, 'credit.id')
    return uniqueCredits.reduce((total, { credit }) => total + credit.balance, 0);
  }

  getRowValues(data, links = true) {
    const row = [];
    let page = null;
    let url = null;
    historyDataTableColumns.forEach(({ id: column }) => {
      const value = data[column];
      switch (column) {
        case 'date':
          if (data.timestamp) {
            const {facilityTimezone} = this.paymentHistory.fieldConfiguration;
            row.push(moment(moment(data.timestamp, 'x').tz(facilityTimezone)));
          } else {
            row.push(null);
          }
          break;
        case 'applicationDate':
          if (value) {
            row.push(moment(value));
          } else {
            row.push(null);
          }
          break;
        case 'type':
          let type;
          if (data.model === 'CREDIT') {
            type = this.paymentHistory.fieldConfiguration.transaction['type'].options.find(
              option => option.value === value
            );
            row.push(PaymentHistory.getType(data.appType, `Credit - ${type?.label}`));
          } else {
            type = this.paymentHistory.fieldConfiguration.transaction['type'].options.find(
              option => option.value === value
            );
            row.push(PaymentHistory.getType(data.appType, type?.label));
          }
          break;
        case 'invoiceLink':
          if (data.invoiceId && data.invoiceNumber) {
            page = `invoice&invoice=${data.invoiceId}`;
            url = `?page=parkchirp_${page}${(this.facilityId !== null) ? `&facility=${this.facilityId}` : ''}`;
            row.push((
              <a href={url}>{data.invoiceNumber}</a>
            ));
          } else {
            row.push(null);
          }
          break;
        case 'creditLink':
          if (data.creditId && data.creditNumber) {
            page = `credit&credit=${data.creditId}`;
            url = `?page=parkchirp_${page}${(this.facilityId !== null) ? `&facility=${this.facilityId}` : ''}`;
            row.push((
              <a href={url}>{data.creditNumber}</a>
            ));
          } else {
            row.push(null);
          }
          break;
        case 'paymentId':
          let pid = null;
          if (data.paymentMeta) {
            const meta = (data.paymentMeta);
            if (meta?.payment?.reference_id) {
              pid = meta?.payment?.reference_id;
            } else if (meta?.check_number) {
              pid = meta?.check_number;
            }
          }
          row.push(pid);
          break;
        case 'notes':
        case 'amount':
        case 'balance':
        case 'creditAmount':
        case 'invoiceId':
        case 'creditId':
        case 'invoiceNumber':
        case 'creditNumber':
          row.push(data[column]);
          break;
        default: break;
      }
    });
    return row;
  }

  prepareResultRows(results) {
    // Iterate over each data set and transform each down to column values
    let rows = PaymentHistory.collectDataRows(results, true).map((row) => this.getRowValues(row));

    // Sort, order, paginate
    const {sortedBy} = this.queryMetadata;
    const sort = sortedBy?.columnId ?? 'date';
    const direction = !sortedBy?.direction && sort === 'date' ? -1 :
      (sortedBy?.direction === 'DESC' || sortedBy?.direction === -1 ? -1 : 1);
    const sortIndex = historyDataTableColumns.findIndex(v => v.id === sort);

    rows.sort((a, b) => {
      switch (sort) {
        case 'date':
        case 'applicationDate':
          if (a[sortIndex] && !b[sortIndex]) {
            return direction;
          } else if (!a[sortIndex] && !b[sortIndex]) {
            return 0;
          } else if (!a[sortIndex] && b[sortIndex]) {
            return -(direction);
          }
          const ad = typeof a[sortIndex] === 'string' ? moment(a[sortIndex]) : a[sortIndex];
          const bd = typeof b[sortIndex] === 'string' ? moment(b[sortIndex]) : b[sortIndex];
          return (ad > bd) ? direction : (ad === bd) ? 0 : -(direction);
        case 'invoiceLink':
          const invoiceNumberIndex = historyDataTableColumns.findIndex(v => v.id === 'invoiceNumber');
          return (a[invoiceNumberIndex] ?? '').localeCompare(b[invoiceNumberIndex] ?? '') * direction;
        case 'creditLink':
          const creditNumberIndex = historyDataTableColumns.findIndex(v => v.id === 'creditNumber');
          return (a[creditNumberIndex] ?? '').localeCompare(b[creditNumberIndex] ?? '') * direction;
        case 'type':
        case 'paymentId':
        case 'notes':
          return (a[sortIndex] ?? '').localeCompare(b[sortIndex] ?? '') * direction;
        case 'amount':
        case 'balance':
        case 'creditAmount':
          const aVal = a[sortIndex] !== null ? parseInt(a[sortIndex]) : 0;
          const bVal = b[sortIndex] !== null ? parseInt(b[sortIndex]) : 0;
          return (aVal - bVal) * direction;
        default:
          return 1;
      }
    });

    // Format transaction and application dates
    const dateIndex = historyDataTableColumns.findIndex(v => v.id === 'date');
    const appDateIndex = historyDataTableColumns.findIndex(v => v.id === 'applicationDate');
    const amountIndex = historyDataTableColumns.findIndex(v => v.id === 'amount');
    const balanceIndex = historyDataTableColumns.findIndex(v => v.id === 'balance');
    const creditAmountIndex = historyDataTableColumns.findIndex(v => v.id === 'creditAmount');

    rows = rows.map((data) => {
      let row = [...data];
      row[dateIndex] = row[dateIndex] ? row[dateIndex].format('M/D/YYYY') : null;
      row[appDateIndex] = row[appDateIndex] ? row[appDateIndex].format('M/D/YYYY') : null;
      row[amountIndex] = PaymentHistory.formatCurrency(row[amountIndex]);
      row[balanceIndex] = PaymentHistory.formatCurrency(row[balanceIndex]);
      row[creditAmountIndex] = PaymentHistory.formatCurrency(row[creditAmountIndex]);
      return row;
    });

    // Pagination
    this.queryMetadata.total = rows.length;
    if (this.queryMetadata.offset) {
      rows.splice(0, this.queryMetadata.offset);
    }
    if (this.queryMetadata.limit) {
      rows.splice(this.queryMetadata.limit);
    }

    return rows;
  }

  _historyFilterInputOnChange(inputId) {
    return value => {
      const filters = _.cloneDeep(this.state.filters);
      filters[inputId] = value;

      this.setState({ filters });
    }
  }

  getColumnIndex(id) {
    return historyDataTableColumns.findIndex(({ id: col }) => col === id);
  }
  handleExportToCsvClick = () => {
    this.setState({
      exportQueryInProgress: true
    }, async () => {
      const { historyDataTableRows } = this.state;

      const invoiceLinkIndex = this.getColumnIndex('invoiceLink');
      const creditLinkIndex = this.getColumnIndex('creditLink');
      const invoiceNumberIndex = this.getColumnIndex('invoiceNumber');
      const creditNumberIndex = this.getColumnIndex('creditNumber');
      // Replace the JSX links with the string representations
      historyDataTableRows.forEach(row => {
        row[invoiceLinkIndex] = row[invoiceNumberIndex];
        row[creditLinkIndex] = row[creditNumberIndex];
        row.splice(-4);
      });

      // Place column headers at the top
      const columns = historyDataTableColumns.filter((col) => col.heading);
      historyDataTableRows.unshift(columns.map(v => v.heading));

      const csvContent = "data:text/csv;charset=utf-8,"
        + historyDataTableRows.map(e => e.join(",")).join("\n");

      const encodedUri = encodeURI(csvContent);

      let linkElement = document.createElement('a');
      linkElement.type = 'text/csv';
      linkElement.download = 'subscription_history.csv';
      linkElement.href = encodedUri;
      linkElement.dispatchEvent(new MouseEvent('click'));
    });

    this.setState({
      exportQueryInProgress: false
    });
  }

  resetFilters = () => {
    const filters = {};
    for(const filter in this.state.filters) {
      filters[filter] = null;
    }

    return filters;
  }

  handleShowUnpaidInvoices = () => {
    const filters = this.resetFilters();

    filters.show_unpaid_invoices = true;

    this.setState({ filters }, () => {
      this.refreshHistoryDataTable();
    });
  }

  handleFilterButtonClick = () => {
    this.refreshHistoryDataTable();
  }

  handleResetFiltersClick = () => {
    const filters = this.resetFilters();

    this.setState({ filters }, () => {
      this.refreshHistoryDataTable();
    });
  }

  handleSortRequest = data => {
    this.queryMetadata.sortedBy = data;
    this.refreshHistoryDataTable();
  }

  handleLimitChange = data => {
    this.queryMetadata.limit = data;
    this.queryMetadata.offset = 0;
    this.refreshHistoryDataTable();
  }


  handlePageChange = data => {
    const offset = ((data - 1) * this.queryMetadata.limit);
    this.queryMetadata.offset = offset;
    this.refreshHistoryDataTable();
  }

  renderPanelInitializationBlock() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <React.Fragment>
        {(!pageInitializationComplete) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Initializing, please wait..." />
          </div>
        }
        {(pageInitializationComplete && pageInitializationErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while initializing the page:</p>
              <ul>
                {pageInitializationErrors.map(error =>
                  <li key={performance.now()}>{error}</li>
                )}
              </ul>
            </div>
          </div>
        }
      </React.Fragment>
    );
  }

  renderPanelFilterForm() {
    const {
      queryInProgress,
      filters,
      totalBalance,
      totalCredits
    } = this.state;

    return (
      <div className="parkchirp--subscription-history--filter-form-container">
        <div className="parkchirp--subscription-history--filter-form">
          <h2>Filter</h2>
          <div className="parkchirp--subscription-history--filter-inputs">
            <div className="parkchirp--subscription-history--filter-input-block">
              <Field label="Invoice #"
                     id="parkchirp--subscription-history-filter--invoice-number"
                     layout="columns">
                <InputTextField inputId="parkchirp--subscription-history-filter--invoice-number"
                                value={filters.invoice_number}
                                onChange={this._historyFilterInputOnChange('invoice_number')}
                                editMode={true}
                />
              </Field>
              <Field label="Start Date"
                     id="parkchirp--subscription-history-filter--start-date"
                     layout="columns">
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <KeyboardDatePicker
                    id="parkchirp--invoice-reports-filter--invoice-end-date"
                    clearable
                    variant="outlined"
                    value={filters.start_date}
                    placeholder=""
                    onChange={this._historyFilterInputOnChange('start_date')}
                    format="MM/DD/yyyy"
                  />
                </MuiPickersUtilsProvider>
              </Field>
              <Field label="End Date"
                     id="parkchirp--subscription-history-filter--end-date"
                     layout="columns">
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <KeyboardDatePicker
                    id="parkchirp--invoice-reports-filter--invoice-end-date"
                    clearable
                    variant="outlined"
                    value={filters.end_date}
                    placeholder=""
                    onChange={this._historyFilterInputOnChange('end_date')}
                    format="MM/DD/yyyy"
                  />
                </MuiPickersUtilsProvider>
              </Field>
            </div>
            <div className="parkchirp--subscription-history--filter-input-block">
              <Field label="Total Balance:"
                     id="parkchirp--subscription-history-filter--total-balance"
                     containerClasses={['parkchirp--subscription-history--total-box']}
                     layout="columns"
              >
                <InputCurrencyField
                  inputId="parkchirp--subscription-history--total-balance"
                  value={totalBalance}
                  editMode={false}
                />
              </Field>
              <Field label="Total Credits:"
                     id="parkchirp--subscription-history-filter--total-credits"
                     containerClasses={['parkchirp--subscription-history--total-box']}
                     layout="columns"
              >
                <InputCurrencyField
                  inputId="parkchirp--subscription-history--total-credits"
                  value={totalCredits}
                  editMode={false}
                />
              </Field>
              <Field id="parkchirp--subscription-history-filter--show-unpaid-invoices"
                     layout="columns">
                <ActionButton label="Show Unpaid Invoices"
                              inProgressState={queryInProgress}
                              onClick={this.handleShowUnpaidInvoices}
                />
              </Field>
            </div>
          </div>
          <div className="parkchirp--subscription-history--filter-actions">
            <button type="button"
                    className="button button-primary parkchirp--subscription-history--filter-action"
                    onClick={this.handleFilterButtonClick}
                    disabled={queryInProgress}>
              Filter
            </button>
            <button type="button"
                    className="button parkchirp--subscription-history--filter-action"
                    onClick={this.handleResetFiltersClick}
                    disabled={queryInProgress}>
              Reset
            </button>
          </div>
        </div>
      </div>
    )
  }

  renderPanelDataTable() {
    const {
      historyDataTableRows,
      exportQueryInProgress,
      queryInProgress,
      queryErrors
    } = this.state;

    const columns = historyDataTableColumns.filter((col) => col.heading);
    const rows = historyDataTableRows.map((row) => row.slice(0, -4));
    return (
      <React.Fragment>
        {(queryInProgress) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Retrieving data, please wait..." />
          </div>
        }
        {(!queryInProgress && queryErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while retrieving the Subscription data:</p>
              <ul>
                {queryErrors.map(error =>
                  <li key={performance.now()}>{error}</li>
                )}
              </ul>
            </div>
          </div>
        }
        {!queryInProgress && queryErrors.length === 0 &&
          <React.Fragment>
            <div className="parkchirp--subscription-history-data-table-actions">
              {(this.queryMetadata.total !== null && this.queryMetadata.total > 0) &&
                <ActionButton label="Export to CSV"
                              inProgressState={exportQueryInProgress}
                              onClick={this.handleExportToCsvClick}
                />
              }
            </div>
            <DataTable
              id="subscription-history"
              rows={rows}
              columns={columns}
              overallRowCount={this.queryMetadata.total}
              currentOffset={this.queryMetadata.offset}
              currentLimit={this.queryMetadata.limit}
              sortedBy={this.queryMetadata.sortedBy}
              onSortRequest={this.handleSortRequest}
              onLimitChange={this.handleLimitChange}
              onPageChange={this.handlePageChange}
            />
          </React.Fragment>
        }
      </React.Fragment>
    );
  }

  render() {
    const {
      panelInitializationComplete,
      panelInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page--subscription-detail--history-panel">
        {(!panelInitializationComplete || panelInitializationErrors.length > 0) &&
          this.renderPanelInitializationBlock()
        }
        {(panelInitializationComplete && panelInitializationErrors.length === 0) &&
          <React.Fragment>
            {this.renderPanelFilterForm()}
            {this.renderPanelDataTable()}
          </React.Fragment>
        }
      </div>
    );
  }
}
export default SubscriptionDetailHistoryPanel;
